// iter-386u-aq — Persistent "Trial ends in Xd Yh" banner shown at the top
// of every authenticated screen for users whose plan_status is 'trialing'.
// Auto-hides after the trial ends or when user upgrades to any paid plan.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Clock } from 'lucide-react';

export default function TrialBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [user, setUser] = useState(null);

  useEffect(() => {
    const read = () => {
      try {
        setUser(JSON.parse(localStorage.getItem('user') || 'null'));
      } catch { /* ignore */ }
    };
    read();
    window.addEventListener('plan-updated', read);
    const tick = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => { clearInterval(tick); window.removeEventListener('plan-updated', read); };
  }, []);

  if (dismissed || !user) return null;
  if (user.role === 'owner' || user.billing_exempt || user.plan_cancelled || user.plan_cancelled_at) return null;
  if (user.plan_status !== 'trialing') return null;
  const endsAtRaw = user.plan_trial_ends_at;
  if (!endsAtRaw) return null;
  const endsAt = new Date(endsAtRaw).getTime();
  if (!Number.isFinite(endsAt)) return null;
  const msLeft = endsAt - now;
  if (msLeft <= 0) return null; // trial ended — silence the banner

  const days = Math.floor(msLeft / 86400000);
  const hours = Math.floor((msLeft % 86400000) / 3600000);
  const urgent = days < 2;
  const bg = urgent ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-amber-50 border-amber-200 text-amber-900';
  const dot = urgent ? 'bg-rose-500' : 'bg-amber-500';

  return (
    <div
      className={`sticky top-0 z-40 border-b ${bg} px-4 py-2 text-xs flex items-center justify-center gap-3`}
      data-testid="trial-banner"
    >
      <span className={`h-2 w-2 rounded-full ${dot} animate-pulse flex-shrink-0`} />
      <Clock size={13} className="opacity-70 flex-shrink-0" />
      <span>
        <strong>Free trial ends in {days > 0 ? `${days}d · ${hours}h` : `${hours}h`}</strong>
        <span className="opacity-80"> · Upgrade to keep your projects, credits and BOQs after the trial.</span>
      </span>
      <Link
        to="/billing"
        className={`ml-2 px-3 py-1 rounded-full text-[11px] font-semibold ${urgent ? 'bg-rose-600 text-white hover:bg-rose-700' : 'bg-amber-600 text-white hover:bg-amber-700'}`}
        data-testid="trial-banner-upgrade"
      >
        Upgrade →
      </Link>
      <button
        onClick={() => setDismissed(true)}
        className="ml-1 opacity-50 hover:opacity-100"
        aria-label="Dismiss"
        data-testid="trial-banner-dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
